import React, { useContext, useEffect, useRef, useState } from 'react';
import { driver, DriveStep, Driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import './styles/showme.css';
import { useShowMe } from './ShowMeProvider';
import useSnackbar from '@hooks/useSnackbar';
import { useTranslation } from 'react-i18next';
import { ModalContext } from '@organisms/Modals/ModalContext';
import { TourStep } from './types';
import { PulseEffect, ContextualOverlay, ContextualDrawer } from './animations';
import { useStepActions } from './hooks/useStepActions';
import { Box, Button, Stack, Typography } from '@mui/material';

function mapStepToDriver(step: TourStep): DriveStep {
  return {
    element: step.target ? `[data-showme="${step.target}"]` : undefined,
    popover: {
      title: step.title,
      description: step.content,
      side: step.position ?? 'bottom',
      align: 'center',
    },
  };
}

export function TourOverlay() {
  const { t } = useTranslation();
  const { displayInfo, displayError } = useSnackbar();
  const {
    isActive,
    currentScenario,
    currentStepIndex,
    resolvedStepOrder,
    nextStep,
    previousStep,
    exitTour,
    makeDecision,
    markUnderstood,
  } = useShowMe();

  const driverRef = useRef<Driver | null>(null);
  const { closeModal } = useContext(ModalContext);
  const { executeActions } = useStepActions({ closeModal });
  const [isExecutingActions, setIsExecutingActions] = useState(false);

  // Helper to get step by ID from resolvedStepOrder
  const getStepById = (index: number): TourStep | null => {
    if (!currentScenario) return null;
    const stepId = resolvedStepOrder[index];
    return currentScenario.steps.find(s => s.id === stepId) ?? null;
  };

  // Get current step
  const currentStep = getStepById(currentStepIndex);
  const isDecisionStep = currentStep?.type === 'decision';
  const isContextualTour = currentScenario?.type === 'contextual';

  // Add body class for contextual tours (to adjust z-index of modal backdrop)
  useEffect(() => {
    if (isActive && isContextualTour) {
      document.body.classList.add('showme-contextual-active');
      return () => {
        document.body.classList.remove('showme-contextual-active');
      };
    }
  }, [isActive, isContextualTour]);

  // Execute step actions when entering a new step
  useEffect(() => {
    if (!isActive || !currentScenario || isDecisionStep) return;

    const stepId = resolvedStepOrder[currentStepIndex];
    const step = currentScenario.steps.find(s => s.id === stepId);
    if (!step?.onEnter || step.onEnter.length === 0) return;

    const runActions = async () => {
      setIsExecutingActions(true);
      try {
        await executeActions(step.onEnter!);
      } catch (error) {
        console.error('[ShowMe] Error executing step actions:', error);
        displayError(t('showme.actionError', 'Une erreur est survenue lors de l\'exécution des actions'));
      } finally {
        setIsExecutingActions(false);
      }
    };

    void runActions();
  }, [isActive, currentScenario, currentStepIndex, resolvedStepOrder, isDecisionStep, executeActions, displayError, t]);

  // Memoize ordered steps to avoid recreating on every render
  const orderedDriverSteps = React.useMemo(() => {
    if (!currentScenario) return [];
    const nonDecisionSteps = resolvedStepOrder
      .map(id => currentScenario.steps.find(s => s.id === id))
      .filter((s): s is TourStep => s !== undefined && s.type !== 'decision');
    return nonDecisionSteps.map(mapStepToDriver);
  }, [currentScenario, resolvedStepOrder]);

  // Get non-decision steps for index calculation
  const nonDecisionSteps = React.useMemo(() => {
    if (!currentScenario) return [];
    return resolvedStepOrder
      .map(id => currentScenario.steps.find(s => s.id === id))
      .filter((s): s is TourStep => s !== undefined && s.type !== 'decision');
  }, [currentScenario, resolvedStepOrder]);

  // Initialize driver ONCE when tour starts (not on every step change)
  // Skip Driver.js entirely for contextual tours - we use ContextualDrawer instead
  useEffect(() => {
    // Don't use Driver.js for decision steps or contextual tours - we render custom UI
    if (!isActive || !currentScenario || isDecisionStep || isContextualTour) {
      // Cleanup if tour ended or showing decision/contextual
      if (driverRef.current && (!isActive || isDecisionStep || isContextualTour)) {
        driverRef.current.destroy();
        driverRef.current = null;
      }
      return;
    }

    // Only create driver if it doesn't exist
    if (driverRef.current) return;

    driverRef.current = driver({
      showProgress: true,
      showButtons: ['next', 'close'],
      steps: orderedDriverSteps,
      overlayColor: 'rgba(0, 0, 0, 0.6)',
      popoverClass: 'showme-popover',
      stagePadding: 10, // Padding around highlighted element
      stageRadius: 8, // Border radius for the cutout
      disableActiveInteraction: true, // User should not click - just watch and click Next
      nextBtnText: t('showme.next', 'Suivant'),
      prevBtnText: t('showme.previous', 'Précédent'),
      doneBtnText: t('showme.understood', "J'ai compris"),
      allowClose: false, // Prevent clicking overlay to close
      onNextClick: () => {
        // Sync with our state management
        nextStep();
      },
      onPrevClick: () => {
        // Sync with our state management
        previousStep();
      },
      onCloseClick: () => {
        // User clicked close button
        if (currentScenario) {
          markUnderstood(currentScenario.id);
          // Only show the "use ? button" message for scenarios accessible from the help menu
          if (currentScenario.showInMenu !== false) {
            displayInfo(t('showme.canReplayFromButton', 'Vous pouvez toujours accéder à ce guide via le bouton ? dans la barre supérieure'));
          }
        }
        exitTour();
      },
      onPopoverRender: (popover) => {
        // Add MUI-styled classes
        popover.wrapper.classList.add('MuiPaper-root', 'MuiPaper-elevation8');
      },
    });

    // Start at step 0 - the navigation effect will handle driving to the correct step
    driverRef.current.drive(0);

    // Cleanup only when tour ends or scenario changes (NOT on step change)
    return () => {
      if (driverRef.current) {
        driverRef.current.destroy();
        driverRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- Intentionally excluding currentStepIndex to prevent driver recreation on every step
  }, [isActive, currentScenario, isDecisionStep, isContextualTour, orderedDriverSteps, nextStep, previousStep, exitTour, markUnderstood, displayInfo, t]);

  // Navigate to the correct step when index changes (without recreating driver)
  // Skip for contextual tours - they don't use Driver.js
  useEffect(() => {
    if (!isActive || !currentScenario || isExecutingActions || isDecisionStep || isContextualTour) return;
    if (!driverRef.current) return;

    // Compute driver index: count non-decision steps before current step
    const currentStepId = resolvedStepOrder[currentStepIndex];
    const step = nonDecisionSteps.find(s => s.id === currentStepId);
    const driverIndex = nonDecisionSteps.findIndex(s => s.id === currentStepId);

    if (driverIndex >= 0 && step) {
      // If step has a tabId and scenario has onTabChange, switch tabs first
      if (step.tabId && currentScenario.onTabChange) {
        currentScenario.onTabChange(step.tabId);
        // Wait for React to re-render after tab switch, then highlight the field
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            driverRef.current?.drive(driverIndex);
          });
        });
      } else {
        driverRef.current.drive(driverIndex);
      }
    }
  }, [isActive, currentScenario, currentStepIndex, resolvedStepOrder, isExecutingActions, isDecisionStep, isContextualTour, nonDecisionSteps]);

  useEffect(() => {
    if (!isActive) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      switch (event.key) {
        case 'Escape':
          exitTour();
          break;
        case 'ArrowRight':
        case 'Enter':
          event.preventDefault();
          nextStep();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isActive, nextStep, exitTour]);

  // Render pulse effect for current step (not for decision steps)
  const pulseStep = getStepById(currentStepIndex);

  // Handle decision option click
  const handleDecisionClick = (optionId: string) => {
    if (!currentStep) return;
    makeDecision(currentStep.id, optionId);
  };

  // Handle contextual tour close
  const handleContextualClose = () => {
    if (currentScenario) {
      markUnderstood(currentScenario.id);
      // Only show the "use ? button" message for scenarios accessible from the help menu
      if (currentScenario.showInMenu !== false) {
        displayInfo(t('showme.canReplayFromButton', 'Vous pouvez toujours accéder à ce guide via le bouton ? dans la barre supérieure'));
      }
    }
    exitTour();
  };

  // Handle contextual tour next (auto-close on last step)
  const handleContextualNext = () => {
    const isLastStep = currentStepIndex >= nonDecisionSteps.length - 1;
    if (isLastStep) {
      if (currentScenario) {
        markUnderstood(currentScenario.id);
      }
      exitTour();
    } else {
      nextStep();
    }
  };

  return (
    <>
      {/* NOTE: Blocking overlay removed - Driver.js handles interaction blocking via disableActiveInteraction */}

      {/* Custom overlay for contextual tours (works inside modals) */}
      {isActive && isContextualTour && pulseStep?.target && !isDecisionStep && (
        <ContextualOverlay targetSelector={`[data-showme="${pulseStep.target}"]`} />
      )}

      {/* Contextual tour drawer - fixed at bottom (including decision steps) */}
      {isActive && isContextualTour && currentStep && (
        <ContextualDrawer
          title={currentStep.title}
          content={currentStep.content}
          currentStep={currentStepIndex}
          totalSteps={nonDecisionSteps.length}
          onNext={handleContextualNext}
          onClose={handleContextualClose}
          nextLabel={t('showme.next', 'Suivant')}
          doneLabel={t('showme.understood', "J'ai compris")}
          decisionOptions={isDecisionStep ? currentScenario?.branches?.[currentStep.id]?.map(opt => ({
            id: opt.id,
            label: opt.label,
          })) : undefined}
          onDecision={handleDecisionClick}
        />
      )}

      {/* Pulse effect for highlighted element (not for decision steps, not for contextual tours) */}
      {pulseStep?.target && !isDecisionStep && !isContextualTour && (
        <PulseEffect targetSelector={`[data-showme="${pulseStep.target}"]`} />
      )}

      {/* Decision step UI - styled to match Driver.js popover (only for non-contextual tours) */}
      {isActive && isDecisionStep && currentStep && !isContextualTour && (
        <>
          {/* Dark overlay matching Driver.js */}
          <div className="driver-overlay showme-decision-overlay" />

          <Box
            className="driver-popover showme-decision-popover"
            sx={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 100002,
              maxWidth: 420,
              width: 'calc(100% - 32px)',
            }}
          >
          {/* Title */}
          <Typography className="driver-popover-title">
            {currentStep.title}
          </Typography>

          {/* Description */}
          <Typography className="driver-popover-description">
            {currentStep.content}
          </Typography>

          {/* Options */}
          <Box sx={{ px: 3, pb: 2 }}>
            <Stack spacing={1.5}>
              {currentScenario?.branches?.[currentStep.id]?.map((option) => (
                <Button
                  key={option.id}
                  variant="contained"
                  size="large"
                  fullWidth
                  onClick={() => handleDecisionClick(option.id)}
                  className="driver-popover-next-btn"
                  sx={{
                    py: 1.25,
                    px: 2.5,
                    borderRadius: '6px',
                    fontWeight: 500,
                    fontSize: '0.875rem',
                    textTransform: 'none',
                  }}
                >
                  {option.label}
                </Button>
              ))}
            </Stack>
          </Box>

        </Box>
        </>
      )}
    </>
  );
}
